import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component"
import ApiMakerTable from "shared/api-maker-table"
import classNames from "classnames"
import Contact from "models/contact"
import ContactLink from "components/contacts/link"
import ContactRelationship from "models/contact-relationship"
import ContactRelationshipTypeLink from "components/contact-relationship-types/link"
import linkProps from "shared/link-props"
import memo from "set-state-compare/build/memo"
import PropTypes from "prop-types"
import React from "react"
import Routes from "shared/routes"

/**
 * @typedef {object} ContactRelationshipTableColumn
 * @property {string} attribute
 * @property {boolean} [defaultVisible]
 * @property {(arg: {contactRelationship: ContactRelationship}) => React.ReactNode} [content]
 * @property {string} [label]
 * @property {Array<string>} [path]
 * @property {string} [sortKey]
 */

/** @type {Array<string>} */
const tablePreload = ["child.contact_type", "child.school_class.class_step", "parent.contact_type", "parent.school_class.class_step"]
/** @type {Record<string, Array<string>>} */
const tableSelect = {
  ClassStep: ["id", "name"],
  Contact: ["firstName", "id", "lastName", "name", "pointsLastThirtyDays"],
  ContactRelationship: ["id"],
  ContactRelationshipType: ["id"],
  ContactType: ["id", "identifier"],
  SchoolClass: ["id"]
}

/** Contact relationships table component. */
/**
 * @typedef {object} ComponentsContactRelationshipsTableProps
 * @property {unknown} adminLayout
 * @property {string} className
 * @property {unknown} relationDirection
 * @property {unknown} userLayout
 */

/** @typedef {object} ComponentsContactRelationshipsTableState */

/**
 * @extends {ShapeComponent<ComponentsContactRelationshipsTableProps, ComponentsContactRelationshipsTableState>}
 */
class ComponentsContactRelationshipsTable extends ShapeComponent {
  static defaultProps = {
    adminLayout: false,
    relationDirection: null,
    userLayout: false
  }

  static propTypes = {
    adminLayout: PropTypes.bool.isRequired,
    className: PropTypes.string,
    relationDirection: PropTypes.oneOf(["child", "parent"]),
    userLayout: PropTypes.bool.isRequired
  }

  /** @returns {React.ReactNode} */
  render() {
    const {columns, editModelPath} = this.tt
    const {adminLayout, className, userLayout, ...restProps} = this.props

    return (
      <ApiMakerTable
        className={classNames("components--contact-relationships--table", className)}
        columns={columns}
        editModelPath={editModelPath}
        modelClass={ContactRelationship}
        preload={tablePreload}
        select={tableSelect}
        {...restProps}
      />
    )
  }

  /** @returns {Array<ContactRelationshipTableColumn>} */
  columns = () => [
    ...(this.p.relationDirection ? this.tt.singleDirectionColumns() : this.tt.defaultColumns())
  ]

  /** @returns {Array<ContactRelationshipTableColumn>} */
  defaultColumns = () => [
    {
      attribute: "id",
      defaultVisible: false,
      sortKey: "id"
    },
    {
      attribute: "name",
      content: this.tt.parentContent,
      label: ContactRelationship.humanAttributeName("parent"),
      path: ["parent"],
      sortKey: "parentName"
    },
    {
      attribute: "name",
      content: this.tt.childContent,
      label: ContactRelationship.humanAttributeName("child"),
      path: ["child"],
      sortKey: "childName"
    },
    {
      attribute: "name",
      content: this.tt.contactRelationshipTypeContent,
      label: ContactRelationship.humanAttributeName("contactRelationshipType"),
      path: ["contactRelationshipType"],
      sortKey: "contactRelationshipTypeName"
    },
    {
      attribute: "createdAt",
      sortKey: "createdAt"
    }
  ]

  /** @returns {Array<ContactRelationshipTableColumn>} */
  singleDirectionColumns = () => [
    {
      attribute: "id",
      defaultVisible: false,
      sortKey: "id"
    },
    {
      attribute: this.tt.relatedContactAttribute(),
      content: this.tt.relatedContactContent,
      label: Contact.humanAttributeName("name")
    },
    {
      attribute: "contactRelationshipTypeId",
      content: this.tt.relatedContactTypeContent,
      label: Contact.humanAttributeName("contactType")
    },
    {
      attribute: "updatedAt",
      content: this.tt.relatedContactPointsLastThirtyDaysContent,
      label: Contact.humanAttributeName("pointsLastThirtyDays")
    },
    {
      attribute: "createdAt",
      sortKey: "createdAt"
    }
  ]

  /** @returns {"childId" | "parentId"} */
  relatedContactAttribute = () => {
    if (this.p.relationDirection == "parent") return "parentId"

    return "childId"
  }

  /** @param {{contactRelationship: ContactRelationship}} arg */
  childContent = ({contactRelationship}) => <ContactLink contact={contactRelationship.child()} {...linkProps(this.props)} />
  /** @param {{contactRelationship: ContactRelationship}} arg */
  contactRelationshipTypeContent = ({contactRelationship}) =>
    <ContactRelationshipTypeLink
      adminLayout
      contactRelationshipType={contactRelationship.contactRelationshipType()}
    />

  /** @param {{contactRelationship: ContactRelationship}} arg */
  parentContent = ({contactRelationship}) => <ContactLink contact={contactRelationship.parent()} {...linkProps(this.props)} />

  /**
   * @param {ContactRelationship} contactRelationship
   * @returns {import("models/contact").default | null}
   */
  relatedContact = (contactRelationship) => {
    if (this.p.relationDirection == "parent") return contactRelationship.parent()
    if (this.p.relationDirection == "child") return contactRelationship.child()

    return null
  }

  /** @param {{contactRelationship: ContactRelationship}} arg */
  relatedContactContent = ({contactRelationship}) => {
    const relatedContact = this.tt.relatedContact(contactRelationship)

    if (!relatedContact) return null

    const schoolClass = relatedContact.schoolClass()

    if (schoolClass && schoolClass.classStep()) {
      return (
        <ContactLink contact={relatedContact} {...linkProps(this.props)}>
          {`${relatedContact.name()} (${schoolClass.classStep().name()})`}
        </ContactLink>
      )
    }

    return <ContactLink contact={relatedContact} {...linkProps(this.props)} />
  }

  /** @param {{contactRelationship: ContactRelationship}} arg */
  relatedContactTypeContent = ({contactRelationship}) => {
    const relatedContact = this.tt.relatedContact(contactRelationship)

    if (!relatedContact || !relatedContact.contactType()) return null

    const identifier = relatedContact.contactType().identifier()

    if (identifier == "school_class") return "class"

    return identifier
  }

  /** @param {{contactRelationship: ContactRelationship}} arg */
  relatedContactPointsLastThirtyDaysContent = ({contactRelationship}) => {
    const relatedContact = this.tt.relatedContact(contactRelationship)

    if (!relatedContact) return null

    return relatedContact.pointsLastThirtyDays()
  }

  /** @param {{contactRelationship: ContactRelationship}} arg */
  editModelPath = ({contactRelationship}) => {
    if (this.p.adminLayout) return Routes.editAdminContactRelationshipPath(contactRelationship.id())
  }
}

export default memo(shapeComponent(ComponentsContactRelationshipsTable))
